import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const UserManagementTable = ({ users, onAction }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'student', label: 'Students' },
    { value: 'institute', label: 'Institutes' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = filterRole === 'all' || user?.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user?.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev?.includes(userId)
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers?.length === filteredUsers?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers?.map(u => u?.id));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-success/10 text-success',
      pending: 'bg-warning/10 text-warning',
      suspended: 'bg-error/10 text-error'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles?.[status]}`}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const styles = {
      student: 'bg-primary/10 text-primary',
      institute: 'bg-secondary/10 text-secondary'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles?.[role]}`}>
        {role?.charAt(0)?.toUpperCase() + role?.slice(1)}
      </span>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row gap-4 mb-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
          </div>
          <div className="flex gap-2">
            <Select
              options={roleOptions}
              value={filterRole}
              onChange={setFilterRole}
              className="w-full lg:w-40"
            />
            <Select
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              className="w-full lg:w-40"
            />
          </div>
        </div>

        {selectedUsers?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium text-foreground">
              {selectedUsers?.length} selected
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="Mail"
              onClick={() => onAction('email', selectedUsers)}
            >
              Send Email
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Ban"
              onClick={() => onAction('suspend', selectedUsers)}
            >
              Suspend
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Trash2"
              onClick={() => onAction('delete', selectedUsers)}
            >
              Delete
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedUsers?.length === filteredUsers?.length && filteredUsers?.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-border"
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">User</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Role</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Registered</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredUsers?.map((user) => (
              <tr key={user?.id} className="hover:bg-muted/50 transition-smooth">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={() => toggleSelectUser(user?.id)}
                    className="w-4 h-4 rounded border-border"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={20} color="var(--color-primary)" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{user?.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {getRoleBadge(user?.role)}
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(user?.status)}
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground data-text">{user?.registeredDate}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onAction('view', [user?.id])}
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onAction('edit', [user?.id])}
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onAction('delete', [user?.id])}
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredUsers?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="Users" size={48} color="var(--color-muted-foreground)" />
          <p className="mt-3 text-muted-foreground">No users found</p>
        </div>
      )}
    </div>
  );
};

export default UserManagementTable;